/**
 * Class definition for `wp-subscribe` element
 *
 * @class WPSubscribe
 * @extends {HTMLElement}
 */
class WPSubscribe extends HTMLElement {
  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-subscribe';
  }

  static get messages() {
    return {
      invalidForm: 'Please enter a name and a valid email address',
      invalidName: 'Please enter a name',
      invalidEmail: 'Please enter a valid email address',
      success: 'Thank you for subscribing',
    };
  }

  get invalid() {
    return this.getAttribute('invalid');
  }

  set invalid(value) {
    if (Boolean(value)) {
      this.setAttribute('invalid', '');
    } else {
      this.removeAttribute('invalid');
    }
  }

  get subscribed() {
    return this.getAttribute('subscribed');
  }

  set subscribed(value) {
    if (Boolean(value)) {
      this.setAttribute('subscribed', '');
    } else {
      this.removeAttribute('subscribed');
    }
  }

  connectedCallback() {
    setTimeout(() => {
      this._initialiseElement();
      // If no search input found, return
      if (!this._form && !this._email) {
        console.warn('No subscribe form found');
        return;
      }

      this._createSuccess();
      this._createError();
      this._addEventListeners();
    });
  }

  _initialiseElement() {
    this._form = this._form || this.querySelector('form');
    this._name = this._name || this.querySelector('input[data-js-name]');
    this._email = this._email || this.querySelector('input[data-js-email]');
    this._nonce = this._nonce || this.querySelector('input[data-js-nonce]');
    this._message = this._message || this.querySelector('div[data-js-message]');
    this._errorMsg = this._errorMsg || '';
    this._button = this._button || this.querySelector('button');
    this._loadingElemnt =
      this._loadingElemnt ||
      this.querySelector('.loading-container--newsletter');
  }

  _addEventListeners() {
    this._form.addEventListener('submit', this._handleSubmit.bind(this));
  }

  _handleSubmit(evt) {
    evt.preventDefault();

    this._loadingElemnt.classList.add('show');
    this._button.setAttribute('disabled', '');

    if (!this._valid()) {
      this._updateError();
      return false;
    }

    this._addSubscriber()
      .then((data) => {
        if (data.status == 200) {
          this._errorMsg = '';
          this._updateSuccess(WPSubscribe.messages.success);
        } else {
          data.json().then((error) => {
            this._errorMsg = typeof error === 'object' ? error.message : error;
            this._updateError();
          });
        }
      })
      .catch((error) => {
        this._errorMsg = typeof error === 'object' ? error.message : error;
        this._updateError();
      });
  }

  _addSubscriber() {
    return fetch('/wp-json/genv/v1/subscribe', {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: this._name.value,
        email: this._email.value,
        subscribe_nonce: this._nonce.value,
      }),
    });
  }

  _valid() {
    if (!this._validName() && !this._validEmail()) {
      this._errorMsg = WPSubscribe.messages.invalidForm;
      return false;
    }
    if (!this._validName()) {
      this._errorMsg = WPSubscribe.messages.invalidName;
      return false;
    }
    if (!this._validEmail()) {
      this._errorMsg = WPSubscribe.messages.invalidEmail;
      return false;
    }
    return true;
  }

  _validName() {
    return this._name.value !== '';
  }

  _validEmail() {
    const hasValue = this._email.value !== '';
    const validEmail =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        this._email.value,
      );
    return hasValue && validEmail;
  }

  _createSuccess() {
    this.success = document.createElement('div');
    this.success.classList.add('c-subscribe__success');
    this._message.appendChild(this.success);
  }

  _createError() {
    this.error = document.createElement('div');
    this.error.classList.add('c-subscribe__error');
    this._message.appendChild(this.error);
  }

  _updateSuccess(message) {
    this._loadingElemnt.classList.remove('show');
    this._button.removeAttribute('disabled');
    this.subscribed = true;
    this.invalid = false;
    this.success.innerText = message;
  }

  _updateError() {
    this._loadingElemnt.classList.remove('show');
    this._button.removeAttribute('disabled');
    this.subscribed = false;
    this.invalid = true;
    this.error.innerText = this._errorMsg;
  }
}

customElements.define(WPSubscribe.is, WPSubscribe);
